import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { sharepointApi } from "../api";
import { QUERY_KEYS } from "../constants";

import type { DocumentLibraryProject } from "@mj/shared-ui";

/**
 * Returns the `resolveProjectFolder` callback `DocumentLibraryView` calls on
 * project selection. Goes through the query cache, so re-selecting a project
 * reuses its folder instead of asking the BFF again.
 */
export const useResolveProjectFolder = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (project: DocumentLibraryProject) =>
      queryClient.fetchQuery({
        queryKey: [
          QUERY_KEYS.projectFolder,
          project.description,
          project.opportunityNo
        ],
        queryFn: () => sharepointApi.resolveFolder(project),
        // A project's root folder rarely changes; the window still lets a
        // folder created mid-session show up without a reload.
        staleTime: 5 * 60 * 1000
      }),
    [queryClient]
  );
};

export const useFolderItems = (folderId: string | null) =>
  useQuery({
    queryKey: [QUERY_KEYS.folderItems, folderId],
    enabled: !!folderId,
    queryFn: () => sharepointApi.getItems(folderId as string)
  });

export const useItemVersions = (itemId: string | null) =>
  useQuery({
    queryKey: [QUERY_KEYS.itemVersions, itemId],
    enabled: !!itemId,
    queryFn: () => sharepointApi.getItemVersions(itemId as string)
  });
